/**
 * Edit View Modal Component
 *
 * Allows users to customize list views by editing name and column visibility.
 * Provides a simple interface for managing which columns appear in the table.
 */

import React, { useState } from 'react';
import { X, Save, Eye, EyeOff, Settings as SettingsIcon } from 'lucide-react';
import type { ListView, ListViewColumn } from '../../types/listView';

interface EditViewModalProps {
  listView: ListView;
  onSave: (updates: Partial<ListView>) => void;
  onClose: () => void;
}

export const EditViewModal: React.FC<EditViewModalProps> = ({
  listView,
  onSave,
  onClose,
}) => {
  const [viewName, setViewName] = useState(listView.name);
  const [columns, setColumns] = useState<ListViewColumn[]>(
    [...listView.columns].sort((a, b) => a.order - b.order)
  );

  // Toggle column visibility
  const toggleColumnVisibility = (fieldApiName: string) => {
    setColumns(
      columns.map((col) =>
        col.fieldApiName === fieldApiName
          ? { ...col, isVisible: !col.isVisible }
          : col
      )
    );
  };

  // Handle save
  const handleSave = () => {
    onSave({
      name: viewName,
      columns: columns,
    });
  };

  // Check if changes were made
  const hasChanges =
    viewName !== listView.name ||
    JSON.stringify(columns) !== JSON.stringify(listView.columns);

  // Count visible columns
  const visibleCount = columns.filter((col) => col.isVisible).length;

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-[#1e293b] rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-500/20 rounded-lg flex items-center justify-center">
              <SettingsIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Edit List View
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Customize columns and view settings
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-[#334155] rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* View Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              View Name
            </label>
            <input
              type="text"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              placeholder="Enter view name..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1e293b] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
            />
          </div>

          {/* Column Visibility */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Visible Columns
              </label>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {visibleCount} of {columns.length} visible
              </span>
            </div>

            {/* Column List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {columns.map((column) => (
                <div
                  key={column.fieldApiName}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    column.isVisible
                      ? 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30'
                      : 'bg-gray-50 dark:bg-[#0f172a] border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {column.fieldLabel}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {column.fieldApiName}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => toggleColumnVisibility(column.fieldApiName)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                      column.isVisible
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                    title={column.isVisible ? 'Hide column' : 'Show column'}
                  >
                    {column.isVisible ? (
                      <>
                        <Eye className="w-4 h-4" />
                        <span className="text-xs font-medium">Visible</span>
                      </>
                    ) : (
                      <>
                        <EyeOff className="w-4 h-4" />
                        <span className="text-xs font-medium">Hidden</span>
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>

            {/* Warning if no columns visible */}
            {visibleCount === 0 && (
              <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  At least one column should be visible in the list view.
                </p>
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="p-4 bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-gray-700 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              About List Views
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              Customize which columns appear in your list view. Hidden columns won't be shown
              in the table but can be revealed anytime. Changes are saved immediately when you
              click Save Changes.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#0f172a]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#334155] rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || !viewName.trim() || visibleCount === 0}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};
